<template>
  <div class="grid items-start gap-4 md:grid-cols-3">
    <div>
      <LabeledText :label="t('renter')">
        <span v-if="customerData.company">{{ customerData.company }}<br /></span>
        {{ customerData.firstName }} {{ customerData.lastName }}<br />
        {{ customerData.street }}<br />
        {{ customerData.zip }} {{ customerData.city }}
      </LabeledText>
    </div>

    <div class="grid grid-cols-1 gap-4">
      <LabeledText :label="t('contactData')">
        <PhoneLink :phone-number="customerData.phone" />
      </LabeledText>

      <LabeledText :label="t('dateOfBirth')">
        {{ formattedDateOfBirth }}
      </LabeledText>

      <LabeledText v-if="customerData.vatId" :label="t('vatId')">
        {{ customerData.vatId }}
      </LabeledText>
    </div>

    <div class="grid grid-cols-1 gap-4">
      <LabeledText v-if="resolvedContactEmail" :label="t('contactEmail')">
        {{ resolvedContactEmail }}
      </LabeledText>
      <div class="flex gap-4">
        <LabeledText :label="t('country')">{{ customerData.country }}</LabeledText>
        <LabeledText :label="t('preferredLanguage')">
          {{ t(`locales.${customerData.preferredLocal}`) }}
        </LabeledText>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LabeledText from '@/components/LabeledText.vue';
import type { CustomerData } from '@/entities/bookings/customer-data';
import { getDateFormat } from '@/hooks/use-date-format';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import PhoneLink from '@/components/PhoneLink.vue';
import { formatCalendarDate } from '@/utils/calendar-date';

const props = defineProps<{
  customerData: CustomerData;
  accountEmail?: string;
}>();

const { t } = useI18n();

const resolvedContactEmail = computed(() => {
  const contactEmail = props.customerData.contactEmail?.trim();
  if (contactEmail) {
    return contactEmail;
  }

  return props.accountEmail?.trim() || undefined;
});

const formattedDateOfBirth = computed(() =>
  props.customerData.dateOfBirth
    ? formatCalendarDate(props.customerData.dateOfBirth, getDateFormat())
    : '',
);
</script>

<i18n lang="json">
{
  "en": {
    "renter": "Renter",
    "contactData": "Contact Data",
    "contactEmail": "Contact email",
    "dateOfBirth": "Date of birth",
    "country": "Country",
    "vatId": "VAT ID",
    "preferredLanguage": "Preferred Language"
  },
  "de": {
    "renter": "Mieter",
    "contactData": "Kontaktdaten",
    "contactEmail": "Kontakt-E-Mail",
    "dateOfBirth": "Geburtsdatum",
    "country": "Land",
    "vatId": "USt-IdNr.",
    "preferredLanguage": "Bevorzugte Sprache"
  },
  "es": {
    "renter": "Arrendatario",
    "contactData": "Datos de contacto",
    "contactEmail": "Correo de contacto",
    "dateOfBirth": "Fecha de nacimiento",
    "country": "País",
    "preferredLanguage": "Idioma preferido"
  }
}
</i18n>
